import React, { useState, useEffect, useMemo } from "react";
import { Nav, Tab, Button } from "react-bootstrap";
import { IoMdHome } from "react-icons/io";
import { IoPeople } from "react-icons/io5";
import { MdWarning } from "react-icons/md";
import { FaBug, FaSignOutAlt, FaCreditCard } from "react-icons/fa";
import * as XLSX from "xlsx";
import "./SuperControlDashboard.css";
import {
  getAllUsers,
  getUpdatedUsers,
  sendTestNotification,
  getAllLoginStats,
  getDailyLoginStats,
} from "./SuperFirebaseFunctions";
import UserModal from "./Robust Components/UserModal";
import { useNavigate } from "react-router-dom";
import { auth } from "../../backend/firebaseFunctions";
import FeedbackModal from "./Robust Components/FeedbackModal";
import {
  getFeedbackReports,
  markFeedbackAsRead,
} from "./SuperFirebaseFunctions";
import {
  GOLD_PACKAGE,
  SILVER_PACKAGE,
  BRONZE_PACKAGE,
} from "../../backend/PAYMENT_PACKAGES_CONSTANTS";
import { IGNORED_DUMMY_ACCOUNTS_IDS } from "./meta/IGNORED_DUMMY_ACCOUNTS";
import PackageUsersModal from "./Robust Components/PackageUsersModal";
import LogoutModal from "./Robust Components/LogoutModal";
import {
  HomeTab,
  UsersTab,
  AlertsTab,
  BugReportsTab,
  PaymentsTab,
} from "./Tabs";
import UserCard from "./Robust Components/UserCard";
import FeedbackReportCard from "./Robust Components/FeedbackReportCard";
import { db } from "../../backend/firebaseFunctions";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";

const CACHE_KEY = "simplycoach_users_cache";
const CACHE_TIMESTAMP_KEY = "simplycoach_users_cache_timestamp";

const SuperControlDashboard = () => {
  const [currentTab, setCurrentTab] = useState("home");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    userId: "",
    userEmail: "",
    userName: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [notificationData, setNotificationData] = useState({
    userId: "",
    messageType: "",
  });
  const [notificationStatus, setNotificationStatus] = useState({
    loading: false,
    success: null,
    error: null,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [bugReports, setBugReports] = useState([]);
  const [bugReportsLoading, setBugReportsLoading] = useState(false);
  const [feedbackReports, setFeedbackReports] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [userStats, setUserStats] = useState({
    activeUsers: 0,
    goldUsers: 0,
    silverUsers: 0,
    bronzeUsers: 0,
    estimatedRevenue: 0,
  });
  const [showPackageUsersModal, setShowPackageUsersModal] = useState(false);
  const [selectedPackageUsers, setSelectedPackageUsers] = useState([]);
  const [selectedPackageType, setSelectedPackageType] = useState("");
  const [loginStats, setLoginStats] = useState({
    mobile: { last24h: 0, last3d: 0, last7d: 0 },
    web: { last24h: 0, last3d: 0, last7d: 0 },
  });
  const [loginStatsLoading, setLoginStatsLoading] = useState(false);
  const [dailyStats, setDailyStats] = useState([]);
  const [dailyStatsLoading, setDailyStatsLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Staged Billings State
  const [stagedBillings, setStagedBillings] = useState([]);
  const [stagedBillingsLoading, setStagedBillingsLoading] = useState(false);
  const [stagedBillingsError, setStagedBillingsError] = useState(null);

  const navigate = useNavigate();

  // Add state for mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getCachedUsers = () => {
    const cachedData = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (cachedData && timestamp) {
      return {
        users: JSON.parse(cachedData),
        timestamp: parseInt(timestamp),
      };
    }
    return null;
  };

  const setCachedUsers = (usersData) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify(usersData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  };

  const mergeUsers = (existingUsers, newUsers) => {
    const userMap = new Map(existingUsers.map((user) => [user.id, user]));

    newUsers.forEach((newUser) => {
      userMap.set(newUser.id, newUser);
    });

    return Array.from(userMap.values());
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const cache = getCachedUsers();

      if (cache) {
        // Use cached data as base
        setUsers(cache.users);

        // Check for updates since last fetch
        const updatedUsers = await getUpdatedUsers(cache.timestamp);
        if (updatedUsers.length > 0) {
          const mergedUsers = mergeUsers(cache.users, updatedUsers);
          setUsers(mergedUsers);
          setCachedUsers(mergedUsers);
        } else {
          // Even if no updates, refresh the cache timestamp to prevent stale data
          setCachedUsers(cache.users);
        }
      } else {
        // Full refresh needed
        const fetchedUsers = await getAllUsers();
        setUsers(fetchedUsers);
        setCachedUsers(fetchedUsers);
      }
    } catch (err) {
      setError("שגיאה בטעינת המשתמשים");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users once when component mounts - force full refresh to ensure latest data
  useEffect(() => {
    handleRefresh();
  }, []); // Empty dependency array means this runs once on mount

  // Ensure users are refreshed when navigating to the users tab
  useEffect(() => {
    if (currentTab === "users") {
      handleRefresh();
    }
  }, [currentTab]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const userIdMatch =
        !filters.userId ||
        user.id.toLowerCase().includes(filters.userId.toLowerCase());
      const emailMatch =
        !filters.userEmail ||
        (user.userEmailAddress &&
          user.userEmailAddress
            .toLowerCase()
            .includes(filters.userEmail.toLowerCase()));
      const nameMatch =
        !filters.userName ||
        (user.userName &&
          user.userName.toLowerCase().includes(filters.userName.toLowerCase()));

      return userIdMatch && emailMatch && nameMatch;
    });
  }, [users, filters]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleCloseModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setNotificationStatus({ loading: true, success: null, error: null });

    try {
      const response = await sendTestNotification(
        notificationData.userId,
        notificationData.messageType
      );

      setNotificationStatus({
        loading: false,
        success: response.message,
        error: null,
      });
    } catch (error) {
      console.error("Error sending notification:", error);
      setNotificationStatus({
        loading: false,
        success: null,
        error: error.message || "Failed to send notification",
      });
    }
  };

  const handleNotificationChange = (e) => {
    const { name, value } = e.target;
    setNotificationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      // Force a full refresh from Firebase
      const fetchedUsers = await getAllUsers();
      setUsers(fetchedUsers);
      setCachedUsers(fetchedUsers);
    } catch (err) {
      setError("שגיאה ברענון המשתמשים");
      console.error("Error refreshing users:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/manager/auth");
    } catch (error) {
      console.error("Error logging out:", error);
      setError("שגיאה ביציאה מהמערכת");
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setIsMobileMenuOpen(false); // Close mobile menu when showing logout modal
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await handleLogout();
  };

  const fetchFeedbackReports = async () => {
    if (currentTab !== "bug-reports") return;

    setFeedbackLoading(true);
    try {
      const reports = await getFeedbackReports();
      setFeedbackReports(reports);
    } catch (error) {
      console.error("Error fetching feedback reports:", error);
      setError("שגיאה בטעינת המשובים");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleMarkFeedbackAsRead = async (feedbackId) => {
    try {
      await markFeedbackAsRead(feedbackId);
      setFeedbackReports((prev) =>
        prev.map((feedback) =>
          feedback.id === feedbackId
            ? { ...feedback, wasRead: true, whenWasRead: new Date() }
            : feedback
        )
      );
    } catch (error) {
      console.error("Error marking feedback as read:", error);
      setError("שגיאה בסימון המשוב כנקרא");
    }
  };

  const handleOpenFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setShowFeedbackModal(true);
  };

  useEffect(() => {
    fetchFeedbackReports();
  }, [currentTab]);

  const fetchLoginStats = async () => {
    if (currentTab !== "home") return;

    setLoginStatsLoading(true);
    try {
      const stats = await getAllLoginStats();
      setLoginStats(stats);
    } catch (error) {
      console.error("Error fetching login stats:", error);
      setError("שגיאה בטעינת נתוני התחברות");
    } finally {
      setLoginStatsLoading(false);
    }
  };

  const fetchDailyStats = async () => {
    if (currentTab !== "home") return;

    setDailyStatsLoading(true);
    try {
      const rawStats = await getDailyLoginStats();

      // Format data for the chart - combine mobile and web data for each date
      const formattedStats = rawStats.mobile.map((mobileDay, index) => {
        const webDay = rawStats.web[index];

        // Parse the date string and create a proper date object
        const [year, month, day] = mobileDay.date.split("-").map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed

        const dayName = date.toLocaleDateString("he-IL", { weekday: "short" });

        return {
          date: mobileDay.date,
          dayName: dayName,
          mobile: mobileDay.count,
          web: webDay.count,
          total: mobileDay.count + webDay.count,
        };
      });

      setDailyStats(formattedStats);
    } catch (error) {
      console.error("Error fetching daily stats:", error);
      setError("שגיאה בטעינת נתוני התחברות יומיים");
    } finally {
      setDailyStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchLoginStats();
    fetchDailyStats();
  }, [currentTab]);

  const calculateUserStats = useMemo(() => {
    if (!users.length)
      return {
        activeUsers: 0,
        goldUsers: 0,
        silverUsers: 0,
        bronzeUsers: 0,
        estimatedRevenue: 0,
      };

    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

    let filteredUsers = users.filter(
      (user) =>
        user.userType !== "coach" &&
        !IGNORED_DUMMY_ACCOUNTS_IDS.includes(user.id)
    );

    let activeUsers = filteredUsers.filter((user) => {
      let lastLogin = null;
      try {
        if (user.lastLoginAt) {
          // Handle Firebase Timestamp objects
          if (user.lastLoginAt.toDate) {
            lastLogin = user.lastLoginAt.toDate();
          }
          // Handle numeric timestamps (milliseconds since epoch)
          else if (typeof user.lastLoginAt === "number") {
            lastLogin = new Date(user.lastLoginAt);
          }
          // Handle date strings
          else if (typeof user.lastLoginAt === "string") {
            lastLogin = new Date(user.lastLoginAt);
          }
          // Handle Firebase Timestamp objects in seconds
          else if (user.lastLoginAt.seconds) {
            lastLogin = new Date(user.lastLoginAt.seconds * 1000);
          }

          // Validate the date
          if (!lastLogin || isNaN(lastLogin.getTime())) {
            console.warn(`Invalid date for user ${user.id}:`, user.lastLoginAt);
            return false;
          }
        }
      } catch (error) {
        console.warn(`Error parsing date for user ${user.id}:`, error);
        return false;
      }

      // If no lastLoginAt, consider user inactive
      if (!lastLogin) {
        return false;
      }

      // Compare dates using getTime() for more precise comparison
      const isActive = lastLogin.getTime() > threeDaysAgo.getTime();

      return isActive;
    }).length;

    const goldUsers = filteredUsers.filter(
      (user) => user.paymentDetails?.plan === "gold"
    ).length;

    const silverUsers = filteredUsers.filter(
      (user) => user.paymentDetails?.plan === "silver"
    ).length;

    const bronzeUsers = filteredUsers.filter(
      (user) => user.paymentDetails?.plan === "bronze"
    ).length;

    const estimatedRevenue =
      goldUsers * GOLD_PACKAGE.PRICE +
      silverUsers * SILVER_PACKAGE.PRICE +
      bronzeUsers * BRONZE_PACKAGE.PRICE;

    return {
      activeUsers,
      goldUsers,
      silverUsers,
      bronzeUsers,
      estimatedRevenue,
    };
  }, [users]);

  useEffect(() => {
    setUserStats(calculateUserStats);
  }, [calculateUserStats]);

  // Add function to handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest(".dashboard-nav-pane")) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  const handlePackageClick = (packageType) => {
    const packageUsers = users.filter(
      (user) =>
        user.paymentDetails?.plan === packageType &&
        user.userType !== "coach" &&
        !IGNORED_DUMMY_ACCOUNTS_IDS.includes(user.id)
    );
    setSelectedPackageUsers(packageUsers);
    setSelectedPackageType(packageType);
    setShowPackageUsersModal(true);
  };

  const exportUsersToExcel = () => {
    // Include ALL non-coach, non-dummy users (no email de-duplication, email optional)
    const customerUsers = users.filter(
      (user) => !IGNORED_DUMMY_ACCOUNTS_IDS.includes(user.id)
    );

    const excelData = customerUsers.map((user, index) => ({
      "CONTACT ID": index + 1,
      EMAIL: user.userEmailAddress || "",
      FIRSTNAME: user.userName?.split(" ")[0] || "",
      LASTNAME: user.userName?.split(" ").slice(1).join(" ") || "",
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

    const date = new Date().toISOString().split("T")[0];
    const filename = `SimplyCoach_Customers_${date}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // Firebase listener for staged billings
  useEffect(() => {
    if (currentTab !== "payments") return;

    setStagedBillingsLoading(true);
    setStagedBillingsError(null);

    try {
      const billingsRef = collection(db, "metadata", "payments", "billings");
      const q = query(
        billingsRef,
        orderBy("billingDocCreatedAt", "desc"),
        limit(1000)
      );

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const billings = [];
          querySnapshot.forEach((doc) => {
            billings.push({
              id: doc.id,
              ...doc.data(),
            });
          });
          setStagedBillings(billings);
          setStagedBillingsLoading(false);
        },
        (error) => {
          console.error("Error fetching staged billings:", error);
          setStagedBillingsError("שגיאה בטעינת החיובים");
          setStagedBillingsLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      console.error("Error setting up staged billings listener:", error);
      setStagedBillingsError("שגיאה בהגדרת מאזין החיובים");
      setStagedBillingsLoading(false);
    }
  }, [currentTab]);

  return (
    <div className="super-dashboard">
      <Tab.Container
        activeKey={currentTab}
        onSelect={(key) => {
          setCurrentTab(key);
          setIsMobileMenuOpen(false); // Close mobile menu when tab changes
        }}
      >
        <div
          className={`dashboard-nav-pane ${
            isMobileMenuOpen ? "mobile-menu-open" : ""
          }`}
        >
          <div className="mobile-menu-toggle d-md-none">
            <Button
              variant={isMobileMenuOpen ? "outline-danger" : "outline-primary"}
              className="w-100 mb-3"
              onClick={toggleMobileMenu}
              aria-label={
                isMobileMenuOpen ? "סגור תפריט ניווט" : "פתח תפריט ניווט"
              }
            >
              {isMobileMenuOpen ? (
                <>
                  <span className="me-2">✕</span>
                  סגור תפריט
                </>
              ) : (
                <>
                  <span className="me-2">☰</span>
                  פתח תפריט
                </>
              )}
            </Button>
          </div>
          <Nav
            variant="pills"
            className={`nav-container ${isMobileMenuOpen ? "show" : ""}`}
          >
            <Nav.Item className="nav-item">
              <Nav.Link eventKey="home" className="nav-link">
                <IoMdHome className="icon" size={30} />
                בית
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="nav-item">
              <Nav.Link eventKey="users" className="nav-link">
                <IoPeople className="icon" size={30} />
                משתמשים
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="nav-item">
              <Nav.Link eventKey="alerts" className="nav-link">
                <MdWarning className="icon" size={30} />
                התרעות
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="nav-item">
              <Nav.Link eventKey="bug-reports" className="nav-link">
                <FaBug className="icon" size={30} />
                משוב משתמשים
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="nav-item">
              <Nav.Link eventKey="payments" className="nav-link">
                <FaCreditCard className="icon" size={30} />
                תשלומים
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <div className="nav-footer">
            <Button
              variant="outline-danger"
              onClick={handleLogoutClick}
              className="logout-button"
            >
              <FaSignOutAlt className="me-2" />
              התנתק
            </Button>
          </div>
        </div>
        <div className="dashboard-content">
          <Tab.Content>
            <Tab.Pane eventKey="home">
              <HomeTab
                userStats={userStats}
                users={users}
                loginStats={loginStats}
                loginStatsLoading={loginStatsLoading}
                dailyStats={dailyStats}
                dailyStatsLoading={dailyStatsLoading}
                handlePackageClick={handlePackageClick}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="users">
              <UsersTab
                users={users}
                loading={loading}
                error={error}
                filters={filters}
                filteredUsers={filteredUsers}
                isRefreshing={isRefreshing}
                handleFilterChange={handleFilterChange}
                handleUserClick={handleUserClick}
                handleRefresh={handleRefresh}
                exportUsersToExcel={exportUsersToExcel}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="alerts">
              <AlertsTab
                notificationData={notificationData}
                notificationStatus={notificationStatus}
                handleNotificationSubmit={handleNotificationSubmit}
                handleNotificationChange={handleNotificationChange}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="bug-reports">
              <BugReportsTab
                feedbackReports={feedbackReports}
                feedbackLoading={feedbackLoading}
                users={users}
                handleOpenFeedback={handleOpenFeedback}
                handleMarkFeedbackAsRead={handleMarkFeedbackAsRead}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="payments">
              <PaymentsTab
                stagedBillings={stagedBillings}
                stagedBillingsLoading={stagedBillingsLoading}
                stagedBillingsError={stagedBillingsError}
                users={users}
              />
            </Tab.Pane>
          </Tab.Content>
        </div>
      </Tab.Container>
      <UserModal
        show={showUserModal}
        handleClose={handleCloseModal}
        user={selectedUser}
      />
      <FeedbackModal
        show={showFeedbackModal}
        handleClose={() => {
          setShowFeedbackModal(false);
          setSelectedFeedback(null);
        }}
        feedback={selectedFeedback}
        onMarkAsRead={handleMarkFeedbackAsRead}
        users={users}
      />
      <PackageUsersModal
        show={showPackageUsersModal}
        handleClose={() => setShowPackageUsersModal(false)}
        users={selectedPackageUsers}
        packageType={selectedPackageType}
      />
      <LogoutModal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
      />
    </div>
  );
};

export default SuperControlDashboard;
